import {
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    makeStyles,
    Paper,
    PaperProps,
} from "@material-ui/core";
import { AutocompleteChangeReason } from "@material-ui/lab";
import * as React from "react";
import Draggable from "react-draggable";

import { createError, RelodeApiError } from "../../../";
import {
    AreaNotification,
    AttachmentLinkCollection,
    Button,
    Chip,
    DialogCloseIconButton,
    Label,
    RelodeTheme,
    SelectInput,
    Text,
    TextArea,
    Upload,
    useAsyncEffect,
    useDeviceSize,
    useEffectAfterMount,
    useNotifications,
} from "../../../components";
import { Attachment } from "../../../modules/types";
import { getRecipients } from "../messaging.service";
import { MessageRecipient } from "../types";
import { useMessageComposerActions } from "./messageComposerActions";
import { messageComposerReducer } from "./messageComposerReducer";
import { initialMessageComposerState } from "./messageComposerState";

/* eslint-disable react-hooks/exhaustive-deps */
const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "move",
            },
            section: {
                marginTop: theme.spacing(4),
            },
            body: {
                margin: 0,
            },
            mui_button_root: {
                margin: 0,
                marginLeft: theme.spacing(1),
            },
            mui_button_root_no_spacing: {
                marginLeft: 0,
                width: "100%",
            },
            mui_actions_root: {
                margin: 0,
                padding: "0 24px 24px",
                display: "flex",
                flexDirection: "column",
            },
            sendButton: { alignSelf: "flex-end" },
            [theme.breakpoints.down("sm")]: {
                sendButton: {
                    width: "100%",
                },
            },
            errorArea: {
                padding: theme.spacing(0, 3),
                margin: theme.spacing(2),
            },
            busy: {
                height: 5,
                backgroundColor: theme.palette.secondary.dark,
            },
            notBusy: {
                height: 5,
                backgroundColor: "transparent",
            },
            recipientGroups: {
                marginBottom: theme.spacing(2),
            },
        }),
    { name: "MessageComposer" }
);

export interface MessageComposerProps {
    jobId?: number;
    jobName?: string;
    leadId?: number;
    leadName?: string;
    initialRecipients?: MessageRecipient[];
    attachments?: Attachment[];
    initialBodyText?: string;
    replyMessageId?: number;
    open?: boolean;

    onClose?: () => void;
}

const MAX_ALLOWED_ATTACHMENTS = 5;
//Equivalent to 10MB - Recommended Maximum Attachment Size in Send Grid
const MAX_ATTACHMENT_SIZE = 26214400;
type RecipientGroup = {
    label: string;
    value: string;
    disabled: boolean;
};
export const MessageComposer = React.memo((props: MessageComposerProps) => {
    const {
        initialRecipients,
        attachments: initialAttachments,
        initialBodyText = "",
        replyMessageId,
        open,
        onClose,
        leadId,
        leadName,
        jobId,
        jobName,
    } = props;

    const [messageComposerState, dispatch] = React.useReducer(
        messageComposerReducer,
        initialMessageComposerState
    );

    const actions = useMessageComposerActions(dispatch);

    const classes = useStyles({});
    const device = useDeviceSize();
    const { actions: notifications } = useNotifications();

    const [recipientItems, setRecipientItems] = React.useState<
        MessageRecipient[]
    >([]);
    const [selectedRecipients, setSelectedRecipients] = React.useState<
        MessageRecipient[]
    >([]);
    const [recipientGroups, setRecipientGroups] = React.useState<
        RecipientGroup[]
    >();
    const [messageRecipients, setMessageRecipients] = React.useState<
        Map<string, MessageRecipient[]>
    >();
    const [body, setBody] = React.useState(initialBodyText);
    const [isValid, setIsValid] = React.useState(false);
    const [uploadError, setUploadError] = React.useState(undefined);

    React.useEffect(() => {
        if (props.open) {
            notifications.closeAreaNotification("messageComposerError");
            setUploadError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    useAsyncEffect(async () => {
        // this code assumes there are no duplicates returned from the API
        try {
            const rec = await getRecipients({
                leadId: leadId || null,
                jobId: jobId || null,
                replyToMessageId: replyMessageId || null,
            });

            const groupArray: RecipientGroup[] = [];
            const groupedRecipients = new Map<string, MessageRecipient[]>();

            rec?.forEach(r =>
                r.groupingNames?.forEach(g => {
                    if (!groupArray.some(x => x.value === g)) {
                        groupArray.push({
                            label: g,
                            value: g,
                            disabled: false,
                        });
                    }
                    if (!groupedRecipients.get(g)) {
                        groupedRecipients.set(g, [r]);
                    } else {
                        groupedRecipients.get(g).push(r);
                    }
                })
            );

            setMessageRecipients(groupedRecipients);
            setRecipientGroups(groupArray);

            if (initialRecipients) {
                // if initial recipient already exists in recipient list use the one from the list
                const init = initialRecipients.map(r => {
                    const match = rec.find(m => m.userId === r.userId);
                    return match ?? r;
                });
                setSelectedRecipients(init);
            }

            //create and set the recipientItems for DownshiftMultiple
            setRecipientItems(rec);

            if (initialAttachments?.length ?? 0 > 0) {
                await actions.copyAttachments(replyMessageId);
            }
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort() || err.isClientError()) return;
            }
            throw createError(
                err,
                "core.messageComposer.useAsyncEffect: get and set recipients"
            );
        }
    }, [jobId, initialRecipients, initialAttachments, replyMessageId]);

    useEffectAfterMount(() => {
        if (
            !messageComposerState.sendingMessage &&
            !messageComposerState.error
        ) {
            handleClose();
            notifications.showSnack("success", "sent message");
        } else if (
            !messageComposerState.sendingMessage &&
            !!messageComposerState.error
        ) {
            if (messageComposerState.errorType === "attachmentUpload") {
                setUploadError(messageComposerState.error);
            } else {
                notifications.showArea(
                    "error",
                    messageComposerState.error,
                    "messageComposerError"
                );
            }
        }
    }, [messageComposerState.sendingMessage, messageComposerState.error]);

    React.useEffect(() => {
        setIsValid(
            !!body && body.trim().length > 0 && selectedRecipients.length > 0
        );
    }, [body, selectedRecipients]);

    const handleClose = () => {
        setBody(initialBodyText);
        actions.clearError();
        actions.clearFileAttachments();
        setSelectedRecipients([]);
        onClose();
    };

    const handleSend = React.useCallback(() => {
        if (selectedRecipients.length === 0 || body.trim().length === 0) return;

        const recipientUserIds: number[] = selectedRecipients.map(
            rc => rc.userId
        );

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        actions.sendMessage({
            jobId,
            leadId: leadId,
            recipientUserIds,
            replyToMessageId: replyMessageId || null,
            body,
            attachments: messageComposerState.attachments?.map(a => ({
                fileId: a.fileId,
            })),
        });
    }, [
        body,
        jobId,
        leadId,
        replyMessageId,
        selectedRecipients,
        actions.sendMessage,
    ]);

    const handleAttachmentsChange = (attachments: Attachment[]) => {
        actions.updateAttachments(attachments);
        setUploadError(undefined);
    };

    // prettier disable
    const handleAttachmentUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setUploadError(undefined);

        if (
            messageComposerState.attachments?.length === MAX_ALLOWED_ATTACHMENTS
        ) {
            setUploadError("max allowed attachments exceeded");
            return;
        }

        const attachmentsSize = messageComposerState.attachments?.reduce(
            (size, attachment) => size + attachment.fileContentLength,
            0
        );

        const totalSize = (attachmentsSize ?? 0) + event.target.files[0].size;
        totalSize > MAX_ATTACHMENT_SIZE
            ? setUploadError(
                  `max allowed total attachment size of ${
                      MAX_ATTACHMENT_SIZE / 1024 / 1024
                  }MB exceeded`
              )
            : await actions.uploadAttachment(event.target.files[0]);
    };

    const addRecipientsByGroup = (groupName: string) => {
        const newSelectedRecipients = messageRecipients
            .get(groupName)
            .filter(
                x =>
                    !selectedRecipients.some(
                        sr => x.displayName === sr.displayName
                    )
            );

        setSelectedRecipients(selectedRecipients.concat(newSelectedRecipients));
    };

    const checkGroupChipSelected = (groupName: string) => {
        const selected = messageRecipients
            .get(groupName)
            .every(mr =>
                selectedRecipients.some(sr => sr.displayName === mr.displayName)
            );

        return selected;
    };

    const title =
        [jobName, leadName].filter(n => !!n).join(" - ") || "questions?";

    const isMobile = window.screen.width <= 600;

    const handleRecipientChange = (
        e: never,
        value: MessageRecipient[],
        reason: AutocompleteChangeReason
    ) => {
        setSelectedRecipients(value);
    };
    return open ? (
        <Dialog
            PaperComponent={
                device === "desktop" ? DraggableComponent : undefined
            }
            aria-labelledby="dialog-draggable-content"
            id="messageDialog"
            open={open}
            scroll="paper"
            onClose={handleClose}
            fullWidth={device === "desktop" ? true : false}
            maxWidth={device === "desktop" ? "sm" : undefined}
            fullScreen={device === "mobile"}
        >
            <DialogTitle
                className={classes.dialogTitle}
                disableTypography={true}
                id="dialog-draggable-content"
            >
                <Text variant="h1" isInline>
                    compose message
                </Text>
                <DialogCloseIconButton
                    onClick={handleClose}
                    id={"messageDialogCloseButton"}
                />
            </DialogTitle>
            <DialogContent>
                {messageComposerState.isUploadingAttachment ? (
                    <LinearProgress
                        color="primary"
                        classes={{ root: classes.busy }}
                    />
                ) : (
                    <div className={classes.notBusy} />
                )}
                <Text dataCy="messageTitle" variant="h4">
                    {title}
                </Text>
                <Label className={classes.section} label="to" fullWidth>
                    <>
                        {recipientGroups?.length > 0 ? (
                            <div className={classes.recipientGroups}>
                                {recipientGroups.map((item, index) => (
                                    <Chip
                                        id={`$menuItem${index}`}
                                        key={item.value}
                                        label={item.label}
                                        className={classes.chip}
                                        data-cy={item.label}
                                        onChipClick={() =>
                                            addRecipientsByGroup(item.value)
                                        }
                                        selected={checkGroupChipSelected(
                                            item.value
                                        )}
                                    />
                                ))}
                            </div>
                        ) : null}
                        <SelectInput<MessageRecipient, true, true>
                            mode="list"
                            multiple
                            clearable
                            id="recipients"
                            value={selectedRecipients}
                            options={recipientItems}
                            label=""
                            getOptionLabel={o =>
                                `${o.displayName} (${o.roleName})`
                            }
                            onChange={handleRecipientChange}
                            limitTags={4}
                            getLimitTagsText={n => `+${n} more`}
                        />
                    </>
                </Label>
                <Upload
                    baseId="messageAttachments"
                    disableButton={messageComposerState.isUploadingAttachment}
                    onUpload={handleAttachmentUpload}
                    uploadErrorMessage={uploadError}
                />
                <AttachmentLinkCollection
                    id="attachmentsList"
                    attachments={messageComposerState.attachments}
                    handleChange={handleAttachmentsChange}
                />
                <Label
                    id="messageBody"
                    className={classes.section}
                    label="message"
                    fullWidth
                >
                    <TextArea
                        id="messageBodyTextArea"
                        data-cy="messageBodyTextArea"
                        className={classes.body}
                        value={body}
                        placeholder="type message here"
                        rows={isMobile ? 5 : 8}
                        fullWidth
                        multiline
                        showCharacterLimit
                        characterLimitSide="left"
                        onChange={e => setBody(e.target.value)}
                        inputProps={{ maxLength: 10240 }}
                    />
                </Label>
                <AreaNotification
                    areaName="messageComposerError"
                    className={classes.errorArea}
                />
            </DialogContent>
            <DialogActions classes={{ root: classes.mui_actions_root }}>
                <Button
                    id="sendMessageButton"
                    data-cy="sendMessageButton"
                    className={classes.sendButton}
                    onClick={handleSend}
                    color="primary"
                    variant="contained"
                    disabled={!isValid || messageComposerState.sendingMessage}
                    size="small"
                    fullWidth={device === "mobile"}
                >
                    send
                </Button>
            </DialogActions>
        </Dialog>
    ) : null;
});

MessageComposer.displayName = "RelodeMessageComposer";

export const DraggableComponent = (props: PaperProps) => (
    <Draggable handle="#dialog-draggable-content">
        <Paper {...props} />
    </Draggable>
);
